import type { ReactElement, ReactNode } from 'react';
import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import BackButton from 'atom-components/buttons/common-back-button';
import Heading from 'atom-components/heading';
import { goBack as nativeGoBack } from 'utils/goBack';
import MainLayoutFooter from './footer';
import { FooterPortalContext } from './footer-portal.provider';
import './index.scss';

type Props = {
  title?: ReactNode;
  subtitle?: ReactNode;
  breadcrumbs?: ReactNode;
  navigationTabs?: ReactNode;
  floatingButtons?: ReactNode;
  footerButtons?: ReactNode;
  onBack?: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  additionalClasses?: {
    wrapper?: string;
    floatingButtons?: string;
    inner?: string;
    header?: string;
    title?: string;
    subtitle?: string;
    content?: string;
    footer?: string;
  };
};

export default function MainLayout(props: React.PropsWithChildren<Props>): ReactElement {
  const navigate = useNavigate();

  const {
    title,
    subtitle,
    breadcrumbs,
    navigationTabs,
    children,
    floatingButtons,
    additionalClasses,
    onBack: onFallBack,
    footerButtons,
    size
  } = props;

  const onBack = useCallback(() => {
    nativeGoBack(() => navigate(-1), onFallBack || ((): void => navigate('/', { replace: true })));
  }, [navigate, onFallBack]);

  const [footerRef, applyFooterRef] = useState<HTMLDivElement | null>(null);

  return (
    <FooterPortalContext.Provider value={applyFooterRef}>
      <div className={clsx('main-layout', additionalClasses?.wrapper)}>
        <div
          className={clsx('main-layout-inner', additionalClasses?.inner, {
            [`size-${size}`]: size
          })}
        >
          <div className={clsx('main-layout-header', additionalClasses?.header)}>
            <div className="d-flex flex-wrap-reverse">
              <div className="flex-grow-1">
                <Heading level={1} className={additionalClasses?.title}>
                  {title}
                </Heading>
                {/* Todo: Migrate older breadcrumbs to the newer semantic Breadcrumb Lite component,
                  using breadcrumbs prop instead of the subtitle prop for these. */}
                {breadcrumbs}
                {!breadcrumbs && (
                  <h2 className={clsx('main-layout-subtitle', additionalClasses?.subtitle)}>
                    {subtitle}
                  </h2>
                )}
              </div>
              <div
                className={clsx('main-layout-floating-buttons', additionalClasses?.floatingButtons)}
              >
                {floatingButtons}
                {!!onBack && <BackButton className="ms-2" onClick={onBack} />}
              </div>
            </div>
            {navigationTabs}
          </div>
          <div className={clsx('main-layout-content', additionalClasses?.content)}>{children}</div>
          {!footerRef ? (
            <MainLayoutFooter
              onBack={onBack}
              footerButtons={footerButtons}
              className={additionalClasses?.footer}
            />
          ) : (
            createPortal(
              <MainLayoutFooter
                onBack={onBack}
                footerButtons={footerButtons}
                className={additionalClasses?.footer}
              />,
              footerRef
            )
          )}
        </div>
      </div>
    </FooterPortalContext.Provider>
  );
}
